import { request, type ApiResult } from '@/utils/request'
import { useUserStore } from '@/store'
import router from '@/router'
import { showToast } from 'vant'

// ==================== DTO 类型 ====================

export interface LoginDTO {
  mobile: string
  password: string
}

export interface RegisterDTO {
  mobile: string
  password: string
  userName?: string
  inviterId?: string | number
  gender?: number
  /** 生日 YYYY-MM-DD（注册选填，可在资料编辑页首次补填，填写后不可修改） */
  birthday?: string
  /** 身高 cm（注册选填，可在资料编辑页首次补填，填写后不可修改） */
  stature?: number
  code?: string
  /** 邀友送花瓣活动专属加密 token（可选，与 inviterId 俱乐部链路独立） */
  inviteToken?: string
  /** 俱乐部推广链接 token（可选，CT1. 前缀；注册后自动加入对应俱乐部） */
  clubToken?: string
  /** 设备指纹标识（邀友活动 24h 防刷） */
  deviceId?: string
}

/** 短信验证码场景：reset_password=重置密码，register=注册 */
export type SmsScene = 'reset_password' | 'register'

export interface ResetPasswordDTO {
  mobile: string
  code: string
  newPassword: string
  confirmPassword: string
}

export interface SmsSendVO {
  sent?: boolean
  /** 是否本地 mock 下发（未真正发短信） */
  mock?: boolean
  /** mock 模式回传的测试码，仅联调使用 */
  devCode?: string
}

export interface LoginUserVO {
  id: number
  oid?: string
  userName?: string
  avatar?: string
  gender?: number
  role?: string
}

export interface AuthTokenVO {
  accessToken: string
  refreshToken: string
  tokenType?: string
  expiresIn?: number
  user?: LoginUserVO
}

// ==================== 接口方法 ====================

/**
 * 手机号 + 密码登录
 * POST /auth/login
 */
export function login(data: LoginDTO): Promise<ApiResult<AuthTokenVO>> {
  return request.post<AuthTokenVO>('/auth/login', data)
}

/**
 * 新用户注册
 * POST /auth/register
 */
export function register(data: RegisterDTO): Promise<ApiResult<AuthTokenVO>> {
  return request.post<AuthTokenVO>('/auth/register', data)
}

/**
 * 发送短信验证码
 * POST /auth/sms/send  body: { mobile, scene }
 * 重置密码场景：后端先校验手机号已注册，未注册返回「该手机号尚未注册」
 */
export function sendSmsCode(mobile: string, scene: SmsScene): Promise<ApiResult<SmsSendVO>> {
  return request.post<SmsSendVO>('/auth/sms/send', { mobile, scene })
}

/**
 * 重置（找回）密码
 * POST /auth/reset-password
 */
export function resetPassword(data: ResetPasswordDTO): Promise<ApiResult<null>> {
  return request.post<null>('/auth/reset-password', data)
}

/**
 * 使用 refreshToken 换取新的 accessToken
 * POST /auth/refresh
 */
export function refreshToken(refreshToken: string): Promise<ApiResult<{ accessToken: string; refreshToken: string }>> {
  return request.post('/auth/refresh', { refreshToken })
}

/**
 * 退出登录：本地清 token，后端可选调用（不存在也直接 resolve）
 */
export async function logout(): Promise<ApiResult<boolean>> {
  const userStore = useUserStore()
  const accessToken = userStore.accessToken
  userStore.logout()

  // 后端接口：POST /auth/logout（可选，失败不影响本地登出）
  try {
    if (accessToken) {
      // 用独立 axios 发一次，避免拦截器 401 又触发登出循环
      const { default: axios } = await import('axios')
      const BASE_URL = import.meta.env.VITE_API_BASE_URL
      await axios.post(
        `${BASE_URL}/auth/logout`,
        {},
        {
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          }
        }
      )
    }
  } catch (_e) {
    // 忽略后端 logout 失败，本地已经清 token
  }

  showToast({ type: 'success', message: '已退出登录' })
  router.replace({ path: '/login' }).catch(() => {})

  return { code: 200, message: 'ok', data: true, timestamp: Date.now() }
}
